import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { Platform, Alert } from 'react-native';
import { router } from 'expo-router';
import { AuthState, User } from '@/types';
import { useTranslation } from './useTranslation';
import { supabaseUrl } from '@/lib/supabase';
import { trackDvLogin, setUserId } from '@/src/analytics';
import { clearCustomIdContext } from '@/src/analytics/customIdContext';
import { setUserId as setFacebookUserId } from '@/src/analytics/facebookTracking';
import { clearCountryCache } from '@/lib/countryDetection';
import { revenueCatInitializer } from '@/services/revenueCatInitializer';
import { cacheWarmingService } from '@/services/cacheWarmingService';
import { oneSignalService } from '@/services/oneSignalService';
import { backgroundRefreshService } from '@/services/backgroundRefreshService';
import { bootSchedulerService } from '@/services/bootSchedulerService';
import { periodicRefreshService } from '@/services/periodicRefreshService';
import { userProfileCacheService } from '@/services/userProfileCacheService';
import { biometricSupportCache } from '@/services/biometricSupportCache';
import { loginCoordinator } from '@/services/loginCoordinator';
import { logoutSignalService } from '@/services/logoutSignalService';
import { generateDefaultPseudo } from '@/utils/pseudoGenerator';

// Conditional imports for platform-specific authentication
let LocalAuthentication: any = null;
let startRegistration: any = null;
let startAuthentication: any = null;
let Purchases: any = null;
let AppleAuthentication: any = null;
let GoogleSignin: any = null;

if (Platform.OS !== 'web') {
  // Only import expo-local-authentication on native platforms
  console.log('🔐 [Auth] Attempting to load LocalAuthentication module...');
  try {
    LocalAuthentication = require('expo-local-authentication');
    console.log('🔐 [Auth] LocalAuthentication module loaded successfully:', {
      moduleExists: !!LocalAuthentication,
      hasHardwareAsync: typeof LocalAuthentication?.hasHardwareAsync,
      isEnrolledAsync: typeof LocalAuthentication?.isEnrolledAsync,
      supportedAuthTypes: typeof LocalAuthentication?.supportedAuthenticationTypesAsync,
    });
  } catch (error) {
    console.error('🔐 [Auth] Failed to load expo-local-authentication:', error);
  }

  // Load Apple Authentication for iOS
  if (Platform.OS === 'ios') {
    try {
      AppleAuthentication = require('expo-apple-authentication');
      console.log('✅ Apple Authentication module loaded successfully');
    } catch (error) {
      console.warn('⚠️ Failed to load Apple Authentication:', error);
      AppleAuthentication = null;
    }
  }

  // Load Google Sign-In for Android and iOS
  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    try {
      const googleSignInModule = require('@react-native-google-signin/google-signin');
      GoogleSignin = googleSignInModule.GoogleSignin;
      console.log('✅ Google Sign-In module loaded successfully');
    } catch (error) {
      console.warn('⚠️ Failed to load Google Sign-In:', error);
      GoogleSignin = null;
    }
  }

  // Load RevenueCat for logout handling
  import('react-native-purchases')
    .then(module => {
      Purchases = module.default;
      console.log('✅ RevenueCat module loaded successfully');
    })
    .catch(error => {
      console.warn('⚠️ Failed to load RevenueCat:', error);
      // Set to null explicitly to prevent undefined access
      Purchases = null;
    });
} else {
  // Only import WebAuthn browser library on web
  import('@simplewebauthn/browser')
    .then(module => {
      startRegistration = module.startRegistration;
      startAuthentication = module.startAuthentication;
      console.log('✅ WebAuthn module loaded successfully');
    })
    .catch(error => {
      console.warn('⚠️ Failed to load WebAuthn browser library:', error);
      // Set to null explicitly to prevent undefined access
      startRegistration = null;
      startAuthentication = null;
    });
}

// Platform-specific storage functions
const getStorageItem = async (key: string): Promise<string | null> => {
  if (Platform.OS === 'web') {
    return localStorage.getItem(key);
  } else {
    const { getItemAsync } = await import('expo-secure-store');
    return await getItemAsync(key);
  }
};

const setStorageItem = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.setItem(key, value);
  } else {
    const { setItemAsync } = await import('expo-secure-store');
    await setItemAsync(key, value);
  }
};

const deleteStorageItem = async (key: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.removeItem(key);
  } else {
    const { deleteItemAsync } = await import('expo-secure-store');
    await deleteItemAsync(key);
  }
};

interface OTPUserData {
  id: string;
  smartuserId: string;
  email?: string;
  phoneNumber?: string;
  isSubscribed: boolean;
  pseudo?: string;
  paymentMethod?: string | null;
}

interface PendingSocialAuthData {
  user: User;
  sessionToken: string;
  isNewUser: boolean;
}

const AuthContext = createContext<{
  authState: AuthState;
  signInWithApple: () => Promise<{ success: boolean; error?: string; shouldRetry?: boolean; needsMarketingConsent?: boolean; isNewUser?: boolean; pendingAuthData?: PendingSocialAuthData }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string; shouldRetry?: boolean; needsMarketingConsent?: boolean; isNewUser?: boolean; pendingAuthData?: PendingSocialAuthData }>;
  completeSocialAuthAfterMarketing: (pendingAuthData: PendingSocialAuthData, authMethod: 'apple' | 'google', marketingConsent?: boolean | null) => Promise<{ success: boolean; error?: string }>;
  loginWithOTPData: (userData: OTPUserData, sessionToken: string, isNewUser: boolean, marketingConsent?: boolean | null) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateUserSubscription: (isSubscribed: boolean) => void;
  updateUserPaymentMethod: (paymentMethod: string | null) => void;
  checkBiometricSupport: () => Promise<{ isAvailable: boolean; isEnrolled: boolean; supportedTypes: string[] }>;
  enableBiometricLogin: (user?: User | null, token?: string | null) => Promise<boolean>;
  disableBiometricLogin: () => Promise<boolean>;
  performBiometricLogin: () => Promise<{ success: boolean; error?: string }>;
  isBiometricEnabled: () => Promise<boolean>;
}>({
  authState: { user: null, token: null, isLoading: true },
  signInWithApple: async () => ({ success: false, error: 'Not implemented' }),
  signInWithGoogle: async () => ({ success: false, error: 'Not implemented' }),
  completeSocialAuthAfterMarketing: async () => ({ success: false, error: 'Not implemented' }),
  loginWithOTPData: async () => ({ success: false, error: 'Not implemented' }),
  logout: async () => {},
  updateUserSubscription: () => {},
  updateUserPaymentMethod: () => {},
  checkBiometricSupport: async () => ({ isAvailable: false, isEnrolled: false, supportedTypes: [] }),
  enableBiometricLogin: async () => false,
  disableBiometricLogin: async () => false,
  performBiometricLogin: async () => ({ success: false }),
  isBiometricEnabled: async () => false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

let authProviderMountCount = 0;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const instanceIdRef = useRef(++authProviderMountCount);
  const isMountedRef = useRef(true);
  const { t, loadUserLanguagePreference } = useTranslation();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
  });

  useEffect(() => {
    console.log(`🔐 [AUTH-PROVIDER] Instance #${instanceIdRef.current} MOUNTED`);
    return () => {
      console.log(`🔐 [AUTH-PROVIDER] Instance #${instanceIdRef.current} UNMOUNTED`);
    };
  }, []);

  useEffect(() => {
    console.log(`🔐 [AUTH-STATE-CHANGE] Instance #${instanceIdRef.current}:`, {
      hasUser: !!authState.user,
      smartuserId: authState.user?.smartuserId?.substring(0, 8) + '...',
      isLoading: authState.isLoading,
    });
  }, [authState]);

  useEffect(() => {
    isMountedRef.current = true;

    // Wrap in async IIFE to properly handle errors
    (async () => {
      try {
        await loadAuthState();
      } catch (error) {
        console.error('🚨 Critical error in loadAuthState:', error);
        // Ensure we set loading to false even on error
        if (isMountedRef.current) {
          setAuthState({
            user: null,
            token: null,
            isLoading: false,
          });
        }
      }
    })();

    // Note: Analytics tracking is initialized by SnowplowInitializer component
    // which ensures country code is available before initialization

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load user's language preference when they log in
  useEffect(() => {
    if (authState.user?.smartuserId && !authState.isLoading) {
      console.log('🌍 User logged in, loading language preference for smartuserId:', authState.user.smartuserId);
      loadUserLanguagePreference(authState.user.smartuserId).catch(error => {
        console.error('Failed to load user language preference:', error);
      });
    }
  }, [authState.user?.smartuserId, authState.isLoading, loadUserLanguagePreference]);

  // Background cache warming on app launch when user is authenticated
  // Note: User preferences (interface language, subtitle language, is_paying) are loaded by bootSchedulerService
  useEffect(() => {
    if (authState.user?.smartuserId && !authState.isLoading) {
      const smartuserId = authState.user.smartuserId;
      logoutSignalService.signalLogin();
      console.log('📦 Triggering background cache warming for smartuserId:', smartuserId);

      // Fire and forget - this runs in the background and doesn't block the UI
      // Note: bootSchedulerService already handles user preferences loading in Phase 1
      cacheWarmingService.warmCacheOnAppLaunch(smartuserId).catch(error => {
        console.error('📦 Background cache warming failed (non-critical):', error);
      });

      // Initialize periodic refresh service for 6-hour background refresh
      periodicRefreshService.initialize();
      periodicRefreshService.setCurrentUser(smartuserId);
    } else if (!authState.user && !authState.isLoading) {
      // User is logged out, clear user from background service
      backgroundRefreshService.setCurrentUser(null);
      periodicRefreshService.setCurrentUser(null);
    }
  }, [authState.user?.smartuserId, authState.isLoading]);

  const loadAuthState = async () => {
    try {
      // Note: Analytics tracking is initialized by SnowplowInitializer component
      // which ensures country code is available before initialization

      console.log('🔐 useAuth: loadAuthState called - checking for active session');

      // Check if user explicitly logged out
      const userLoggedOut = await getStorageItem('USER_LOGGED_OUT');
      console.log('🔐 useAuth: User logged out flag:', userLoggedOut);

      if (userLoggedOut === 'true') {
        // User explicitly logged out - do not restore session
        console.log('🔐 useAuth: User explicitly logged out, staying logged out');
        if (isMountedRef.current) {
          setAuthState({
            user: null,
            token: null,
            isLoading: false,
          });
        }
        return;
      }

      // User did not explicitly log out - attempt to restore session
      // Parallelize storage reads for better performance
      const [token, userString] = await Promise.all([
        getStorageItem('token'),
        getStorageItem('user')
      ]);

      console.log('🔐 useAuth: Has stored token:', !!token);
      console.log('🔐 useAuth: Has stored user:', !!userString);

      if (token && userString) {
        loginCoordinator.beginLogin('restore');

        try {
          const user = JSON.parse(userString);
          console.log('🔐 useAuth: ✅ Restoring session for user:', user.smartuserId);

          if (isMountedRef.current) {
            setAuthState({
              user,
              token,
              isLoading: false,
            });
          }

          try {
            await setUserId(user.smartuserId);
            await setFacebookUserId(user.smartuserId);
            backgroundRefreshService.setCurrentUser(user.smartuserId);
          } catch (analyticsError) {
            console.warn('Failed to set user ID for analytics:', analyticsError);
          }

          try {
            await trackDvLogin({
              type_of_action: 'login',
              method: 'auto_token',
              status: 'ok',
              type: 'null'
            });
          } catch (trackingError) {
            console.warn('Failed to track session restore event:', trackingError);
          }

          bootSchedulerService.startBoot(user.smartuserId).catch(error => {
            console.warn('🔐 useAuth: Boot scheduler failed (non-critical):', error);
          });

          loginCoordinator.commitLogin({
            userId: user.smartuserId,
            sessionToken: token,
            loginMethod: 'restore',
            subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
          });

          console.log('🔐 useAuth: ✅ Session restored successfully');
        } catch (parseError) {
          console.error('🔐 useAuth: Error parsing stored user data:', parseError);
          loginCoordinator.cancelLogin('Session restore failed: parse error');

          try {
            await trackDvLogin({
              type_of_action: 'login',
              method: 'auto_token',
              status: 'ko',
              type: 'null'
            });
          } catch (trackingError) {
            console.warn('Failed to track session restore failure:', trackingError);
          }

          await deleteStorageItem('token');
          await deleteStorageItem('user');
          if (isMountedRef.current) {
            setAuthState({
              user: null,
              token: null,
              isLoading: false,
            });
          }
        }
      } else {
        // No session to restore
        console.log('🔐 useAuth: No session found, user is logged out');
        if (isMountedRef.current) {
          setAuthState({
            user: null,
            token: null,
            isLoading: false,
          });
        }
      }
    } catch (error) {
      console.error('Error loading auth state:', error);
      if (isMountedRef.current) {
        setAuthState({
          user: null,
          token: null,
          isLoading: false,
        });
        console.log('🔐 useAuth: Error loading auth state, user set to logged out');
      }
    }
  };

  const signInWithAppleWeb = async (): Promise<{
    success: boolean;
    error?: string;
    shouldRetry?: boolean;
    needsMarketingConsent?: boolean;
    isNewUser?: boolean;
    pendingAuthData?: { user: User; sessionToken: string; isNewUser: boolean };
  }> => {
    try {
      console.log('🍎 [APPLE AUTH WEB] Starting Apple Sign In for web...');

      const { loadAppleSignInJS, signInWithAppleWeb: appleWebSignIn, getAppleWebRedirectUri } = await import('@/services/webSocialAuthService');
      const loaded = await loadAppleSignInJS();
      if (!loaded) {
        loginCoordinator.cancelLogin('Apple Sign In JS failed to load');
        return { success: false, error: 'Apple Sign In is not available' };
      }

      const appleResult = await appleWebSignIn();
      if (!appleResult) {
        loginCoordinator.cancelLogin('User cancelled');
        return { success: false, error: 'CANCELLED' };
      }

      console.log('🍎 [APPLE AUTH WEB] Calling manage-users with auth_social action...');
      const redirectUri = getAppleWebRedirectUri();

      const appleAuthResponse = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'auth_social',
          appleAuthCode: appleResult.authorizationCode,
          appleEmail: appleResult.email || null,
          appleFullName: appleResult.fullName || null,
          platform: 'web',
          redirectUri,
        }),
      });

      if (!appleAuthResponse.ok) {
        const errorData = await appleAuthResponse.json();
        if (errorData.errorType === 'ACCOUNT_MERGE_NOT_SUPPORTED') {
          loginCoordinator.cancelLogin('Account merge not supported');
          return { success: false, error: t('accountMergeNotSupported') };
        }
        if (errorData.retryable) {
          loginCoordinator.cancelLogin('Apple auth expired');
          return { success: false, error: errorData.details || 'Apple authentication expired. Please try again.', shouldRetry: true };
        }
        loginCoordinator.cancelLogin(errorData.error || 'Apple authentication failed');
        return { success: false, error: errorData.error || 'Apple authentication failed' };
      }

      const appleAuthData = await appleAuthResponse.json();
      if (!appleAuthData.success) {
        loginCoordinator.cancelLogin(appleAuthData.error || 'Apple authentication failed');
        return { success: false, error: appleAuthData.error || 'Apple authentication failed' };
      }

      const user: User = {
        id: appleAuthData.user.id,
        email: appleAuthData.user.email || '',
        phone_number: appleAuthData.user.phoneNumber || '',
        pseudo: appleAuthData.user.pseudo || generateDefaultPseudo(appleAuthData.user.smartuserId),
        isSubscribed: appleAuthData.user.isSubscribed,
        smartuserId: appleAuthData.user.smartuserId,
        paymentMethod: appleAuthData.user.paymentMethod || null,
      };

      if (appleAuthData.needsMarketingConsent) {
        loginCoordinator.cancelLogin('Deferred for marketing consent');
        return {
          success: true,
          needsMarketingConsent: true,
          isNewUser: appleAuthData.isNewUser,
          pendingAuthData: { user, sessionToken: appleAuthData.sessionToken, isNewUser: appleAuthData.isNewUser },
        };
      }

      await setStorageItem('token', appleAuthData.sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({ user, token: appleAuthData.sessionToken, isLoading: false });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);
        await trackDvLogin({
          type_of_action: appleAuthData.isNewUser ? 'account_creation' : 'login',
          method: 'manual_appleconnect',
          status: 'ok',
          type: 'login'
        });
        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn('Failed to track Apple web authentication event:', analyticsError);
      }

      console.log(`🍎 [APPLE AUTH WEB] ${appleAuthData.isNewUser ? 'Signup' : 'Signin'} successful`);

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: appleAuthData.sessionToken,
        loginMethod: 'apple',
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };
    } catch (error: any) {
      console.error('🍎 [APPLE AUTH WEB] Error:', error);
      loginCoordinator.cancelLogin(error?.message || 'Apple Sign In failed');

      try {
        await trackDvLogin({ type_of_action: 'login', method: 'manual_appleconnect', status: 'ko', type: 'login' });
      } catch (analyticsError) {
        console.warn('Failed to track Apple web authentication failure:', analyticsError);
      }

      return { success: false, error: error?.message || 'Apple Sign In failed' };
    }
  };

  const signInWithGoogleWebFlow = async (): Promise<{
    success: boolean;
    error?: string;
    shouldRetry?: boolean;
    needsMarketingConsent?: boolean;
    isNewUser?: boolean;
    pendingAuthData?: { user: User; sessionToken: string; isNewUser: boolean };
  }> => {
    try {
      console.log('🔵 [GOOGLE AUTH WEB] Starting Google Sign In for web...');

      const { loadGoogleIdentityServices, signInWithGoogleWeb: googleWebSignIn } = await import('@/services/webSocialAuthService');
      const loaded = await loadGoogleIdentityServices();
      if (!loaded) {
        loginCoordinator.cancelLogin('Google Identity Services failed to load');
        return { success: false, error: 'Google Sign In is not available' };
      }

      const googleResult = await googleWebSignIn();
      if (!googleResult) {
        loginCoordinator.cancelLogin('User cancelled');
        return { success: false, error: 'CANCELLED' };
      }

      console.log('🔵 [GOOGLE AUTH WEB] Calling manage-users with auth_social action...');

      const googleAuthResponse = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'auth_social',
          googleIdToken: googleResult.idToken,
          platform: 'web',
        }),
      });

      if (!googleAuthResponse.ok) {
        const errorData = await googleAuthResponse.json();
        if (errorData.errorType === 'ACCOUNT_MERGE_NOT_SUPPORTED') {
          loginCoordinator.cancelLogin('Account merge not supported');
          return { success: false, error: t('accountMergeNotSupported') };
        }
        if (errorData.retryable) {
          loginCoordinator.cancelLogin('Google auth expired');
          return { success: false, error: errorData.details || 'Google authentication expired. Please try again.', shouldRetry: true };
        }
        loginCoordinator.cancelLogin(errorData.error || 'Google authentication failed');
        return { success: false, error: errorData.error || 'Google authentication failed' };
      }

      const googleAuthData = await googleAuthResponse.json();
      if (!googleAuthData.success) {
        loginCoordinator.cancelLogin(googleAuthData.error || 'Google authentication failed');
        return { success: false, error: googleAuthData.error || 'Google authentication failed' };
      }

      const user: User = {
        id: googleAuthData.user.id,
        email: googleAuthData.user.email || '',
        phone_number: googleAuthData.user.phoneNumber || '',
        pseudo: googleAuthData.user.pseudo || generateDefaultPseudo(googleAuthData.user.smartuserId),
        isSubscribed: googleAuthData.user.isSubscribed,
        smartuserId: googleAuthData.user.smartuserId,
        paymentMethod: googleAuthData.user.paymentMethod || null,
      };

      if (googleAuthData.needsMarketingConsent) {
        loginCoordinator.cancelLogin('Deferred for marketing consent');
        return {
          success: true,
          needsMarketingConsent: true,
          isNewUser: googleAuthData.isNewUser,
          pendingAuthData: { user, sessionToken: googleAuthData.sessionToken, isNewUser: googleAuthData.isNewUser },
        };
      }

      await setStorageItem('token', googleAuthData.sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({ user, token: googleAuthData.sessionToken, isLoading: false });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);
        await trackDvLogin({
          type_of_action: googleAuthData.isNewUser ? 'account_creation' : 'login',
          method: 'manual_google',
          status: 'ok',
          type: 'login'
        });
        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn('Failed to track Google web authentication event:', analyticsError);
      }

      console.log(`🔵 [GOOGLE AUTH WEB] ${googleAuthData.isNewUser ? 'Signup' : 'Signin'} successful`);

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: googleAuthData.sessionToken,
        loginMethod: 'google',
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };
    } catch (error: any) {
      console.error('🔵 [GOOGLE AUTH WEB] Error:', error);
      loginCoordinator.cancelLogin(error?.message || 'Google Sign In failed');

      try {
        await trackDvLogin({ type_of_action: 'login', method: 'manual_google', status: 'ko', type: 'login' });
      } catch (analyticsError) {
        console.warn('Failed to track Google web authentication failure:', analyticsError);
      }

      return { success: false, error: error?.message || 'Google Sign In failed' };
    }
  };

  const signInWithApple = async (): Promise<{
    success: boolean;
    error?: string;
    shouldRetry?: boolean;
    needsMarketingConsent?: boolean;
    isNewUser?: boolean;
    pendingAuthData?: {
      user: User;
      sessionToken: string;
      isNewUser: boolean;
    };
  }> => {
    loginCoordinator.beginLogin('apple');

    try {
      console.log('🍎 [APPLE AUTH] Starting Apple Sign In...');

      if (Platform.OS === 'web') {
        return await signInWithAppleWeb();
      }

      if (Platform.OS !== 'ios' || !AppleAuthentication) {
        console.error('🍎 [APPLE AUTH] Apple Authentication not available');
        loginCoordinator.cancelLogin('Apple Sign In not available on this platform');
        return { success: false, error: 'Apple Sign In is only available on iOS' };
      }

      const isAvailable = await AppleAuthentication.isAvailableAsync();
      if (!isAvailable) {
        console.error('🍎 [APPLE AUTH] Apple Sign In not available on this device');
        loginCoordinator.cancelLogin('Apple Sign In not available on device');
        return { success: false, error: 'Apple Sign In is not available on this device' };
      }

      const requestStartTime = Date.now();
      console.log('🍎 [APPLE AUTH] Requesting Apple credentials...');
      console.log('🍎 [APPLE AUTH] ⏱️ Request start time:', new Date().toISOString());

      // Request Apple credentials
      let credential;
      try {
        credential = await AppleAuthentication.signInAsync({
          requestedScopes: [
            AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
            AppleAuthentication.AppleAuthenticationScope.EMAIL,
          ],
        });
      } catch (e: any) {
        if (e.code === 'ERR_CANCELED') {
          console.log('🍎 [APPLE AUTH] User cancelled Apple Sign In');
          loginCoordinator.cancelLogin('User cancelled');
          return { success: false, error: 'CANCELLED' };
        }
        throw e;
      }

      const credentialReceivedTime = Date.now();
      const credentialDuration = credentialReceivedTime - requestStartTime;

      console.log('🍎 [APPLE AUTH] Apple credentials received:', {
        hasAuthorizationCode: !!credential.authorizationCode,
        authCodeLength: credential.authorizationCode?.length || 0,
        hasEmail: !!credential.email,
        hasFullName: !!credential.fullName,
      });
      console.log('🍎 [APPLE AUTH] ⏱️ Credential received at:', new Date().toISOString());
      console.log('🍎 [APPLE AUTH] ⏱️ Time to get credentials:', credentialDuration + 'ms');

      // Call unified manage-users endpoint with auth_social action
      console.log('🍎 [APPLE AUTH] Calling manage-users endpoint with auth_social action...');
      console.log('🍎 [APPLE AUTH] ⏱️ API call start time:', new Date().toISOString());

      const apiCallStartTime = Date.now();
      const appleAuthResponse = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'auth_social',
          appleAuthCode: credential.authorizationCode,
          appleEmail: credential.email,
          appleFullName: credential.fullName ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : null,
        }),
      });

      const apiCallEndTime = Date.now();
      const apiCallDuration = apiCallEndTime - apiCallStartTime;
      const totalDuration = apiCallEndTime - requestStartTime;

      console.log('🍎 [APPLE AUTH] ⏱️ API call end time:', new Date().toISOString());
      console.log('🍎 [APPLE AUTH] ⏱️ API call duration:', apiCallDuration + 'ms');
      console.log('🍎 [APPLE AUTH] ⏱️ Total time (credentials + API):', totalDuration + 'ms');
      console.log('🍎 [APPLE AUTH] ⏱️ Time between credential and API call:', (apiCallStartTime - credentialReceivedTime) + 'ms');

      if (!appleAuthResponse.ok) {
        const errorData = await appleAuthResponse.json();

        if (errorData.errorType === 'ACCOUNT_MERGE_NOT_SUPPORTED') {
          console.log('🍎 [APPLE AUTH] Account merge not supported - user should login with original method');
          loginCoordinator.cancelLogin('Account merge not supported');
          return { success: false, error: t('accountMergeNotSupported') };
        }

        console.error('🍎 [APPLE AUTH] Authentication failed:', errorData);
        console.error('🍎 [APPLE AUTH] Failed after:', totalDuration + 'ms');

        if (errorData.retryable) {
          loginCoordinator.cancelLogin('Apple auth expired');
          return { success: false, error: errorData.details || 'Apple authentication expired. Please try again.', shouldRetry: true };
        }

        loginCoordinator.cancelLogin(errorData.error || 'Apple authentication failed');
        return { success: false, error: errorData.error || 'Apple authentication failed' };
      }

      const appleAuthData = await appleAuthResponse.json();

      if (!appleAuthData.success) {
        loginCoordinator.cancelLogin(appleAuthData.error || 'Apple authentication failed');
        return { success: false, error: appleAuthData.error || 'Apple authentication failed' };
      }

      console.log('🍎 [APPLE AUTH] Authentication successful:', {
        isNewUser: appleAuthData.isNewUser,
        needsMarketingConsent: appleAuthData.needsMarketingConsent,
        userId: appleAuthData.user.smartuserId,
      });

      const user: User = {
        id: appleAuthData.user.id,
        email: appleAuthData.user.email || '',
        phone_number: appleAuthData.user.phoneNumber || '',
        pseudo: appleAuthData.user.pseudo || generateDefaultPseudo(appleAuthData.user.smartuserId),
        isSubscribed: appleAuthData.user.isSubscribed,
        smartuserId: appleAuthData.user.smartuserId,
        paymentMethod: appleAuthData.user.paymentMethod || null,
      };

      if (appleAuthData.needsMarketingConsent) {
        console.log('🍎 [APPLE AUTH] Marketing consent required, deferring auth completion...');
        loginCoordinator.cancelLogin('Deferred for marketing consent');
        return {
          success: true,
          needsMarketingConsent: true,
          isNewUser: appleAuthData.isNewUser,
          pendingAuthData: {
            user,
            sessionToken: appleAuthData.sessionToken,
            isNewUser: appleAuthData.isNewUser,
          },
        };
      }

      await setStorageItem('token', appleAuthData.sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({
          user: user,
          token: appleAuthData.sessionToken,
          isLoading: false,
        });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);
        await trackDvLogin({
          type_of_action: appleAuthData.isNewUser ? 'account_creation' : 'login',
          method: 'manual_appleconnect',
          status: 'ok',
          type: 'login'
        });

        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn('Failed to track Apple authentication event:', analyticsError);
      }

      if (Platform.OS !== 'web' && oneSignalService.isReady()) {
        try {
          await oneSignalService.login(user.smartuserId);
        } catch (oneSignalError) {
          console.warn('[OneSignal] Error during login flow:', oneSignalError);
        }
      }

      console.log(`🍎 [APPLE AUTH] ✅ ${appleAuthData.isNewUser ? 'Signup' : 'Signin'} successful`);

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: appleAuthData.sessionToken,
        loginMethod: 'apple',
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };

    } catch (error: any) {
      const errorMessage = error?.message || '';
      const isCancelled =
        error?.code === 'ERR_CANCELED' ||
        errorMessage.includes('user canceled') ||
        errorMessage.includes('authorization attempt');

      if (isCancelled) {
        console.log('🍎 [APPLE AUTH] User cancelled sign in');
        loginCoordinator.cancelLogin('User cancelled');
        return { success: false, error: 'CANCELLED' };
      }

      console.error('🍎 [APPLE AUTH] ❌ Error:', error);
      loginCoordinator.cancelLogin(errorMessage || 'Apple Sign In failed');

      try {
        await trackDvLogin({
          type_of_action: 'login',
          method: 'manual_appleconnect',
          status: 'ko',
          type: 'login'
        });
      } catch (analyticsError) {
        console.warn('Failed to track Apple authentication failure event:', analyticsError);
      }

      const message = typeof errorMessage === 'string' ? errorMessage : 'Apple Sign In failed';
      return { success: false, error: message };
    }
  };

  const signInWithGoogle = async (): Promise<{
    success: boolean;
    error?: string;
    shouldRetry?: boolean;
    needsMarketingConsent?: boolean;
    isNewUser?: boolean;
    pendingAuthData?: {
      user: User;
      sessionToken: string;
      isNewUser: boolean;
    };
  }> => {
    loginCoordinator.beginLogin('google');

    try {
      console.log('🔵 [GOOGLE AUTH] Starting Google Sign In...');

      if (Platform.OS === 'web') {
        return await signInWithGoogleWebFlow();
      }

      if ((Platform.OS !== 'android' && Platform.OS !== 'ios') || !GoogleSignin) {
        console.error('🔵 [GOOGLE AUTH] Google Sign-In not available');
        loginCoordinator.cancelLogin('Google Sign In not available on this platform');
        return { success: false, error: 'Google Sign In is only available on Android and iOS' };
      }

      if (Platform.OS === 'android') {
        console.log('🔵 [GOOGLE AUTH] Checking Google Play Services...');
        const hasPlayServices = await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
        if (!hasPlayServices) {
          console.error('🔵 [GOOGLE AUTH] Google Play Services not available');
          loginCoordinator.cancelLogin('Google Play Services not available');
          return { success: false, error: 'Google Play Services not available on this device' };
        }
      } else {
        console.log('🔵 [GOOGLE AUTH] Skipping Play Services check (iOS)');
      }

      console.log('🔵 [GOOGLE AUTH] Requesting Google credentials...');
      const requestStartTime = Date.now();

      let userInfo;
      try {
        userInfo = await GoogleSignin.signIn();
      } catch (e: any) {
        if (e.code === 'SIGN_IN_CANCELLED') {
          console.log('🔵 [GOOGLE AUTH] User cancelled Google Sign In');
          loginCoordinator.cancelLogin('User cancelled');
          return { success: false, error: 'CANCELLED' };
        }
        if (e.code === 'IN_PROGRESS') {
          console.log('🔵 [GOOGLE AUTH] Sign in already in progress');
          loginCoordinator.cancelLogin('Sign in already in progress');
          return { success: false, error: 'Sign in already in progress' };
        }
        if (e.code === 'PLAY_SERVICES_NOT_AVAILABLE') {
          console.error('🔵 [GOOGLE AUTH] Google Play Services not available');
          loginCoordinator.cancelLogin('Play Services not available');
          return { success: false, error: 'Google Play Services not available' };
        }
        throw e;
      }

      // ============================================
      // === DIAGNOSTIC COMPLET USERINFO ===
      // ============================================
      console.log('🔵 [GOOGLE AUTH] ================================');
      console.log('🔵 [GOOGLE AUTH] FULL USERINFO DIAGNOSTIC');
      console.log('🔵 [GOOGLE AUTH] ================================');

      // Structure de l'objet
      console.log('🔵 [GOOGLE AUTH] UserInfo keys:', Object.keys(userInfo));
      console.log('🔵 [GOOGLE AUTH] UserInfo type:', typeof userInfo);

      // Vérification détaillée des propriétés
      const diagnosticInfo = {
        // IdToken
        hasIdToken: 'idToken' in userInfo,
        idTokenValue: userInfo.idToken,
        idTokenType: typeof userInfo.idToken,
        idTokenLength: userInfo.idToken?.length || 0,
        idTokenIsNull: userInfo.idToken === null,
        idTokenIsUndefined: userInfo.idToken === undefined,

        // ServerAuthCode
        hasServerAuthCode: 'serverAuthCode' in userInfo,
        serverAuthCodeValue: userInfo.serverAuthCode,
        serverAuthCodeType: typeof userInfo.serverAuthCode,

        // User object
        hasUser: 'user' in userInfo,
        userKeys: userInfo.user ? Object.keys(userInfo.user) : [],
        userEmail: userInfo.user?.email,
        userId: userInfo.user?.id,

        // Scopes
        hasScopes: 'scopes' in userInfo,
        scopesValue: userInfo.scopes,

        // Autres propriétés potentielles
        hasData: 'data' in userInfo,
        hasCredentials: 'credentials' in userInfo,
        hasAuthentication: 'authentication' in userInfo,
        allKeys: Object.keys(userInfo)
      };

      console.log('🔵 [GOOGLE AUTH] Diagnostic:', JSON.stringify(diagnosticInfo, null, 2));

      // Si idToken existe, analyser le JWT
      if (userInfo.idToken) {
        const preview = userInfo.idToken.substring(0, 50) + '...' + userInfo.idToken.substring(userInfo.idToken.length - 20);
        console.log('🔵 [GOOGLE AUTH] IdToken preview:', preview);

        try {
          const parts = userInfo.idToken.split('.');
          if (parts.length === 3) {
            const header = JSON.parse(atob(parts[0]));
            const payload = JSON.parse(atob(parts[1]));
            console.log('🔵 [GOOGLE AUTH] Token header:', header);
            console.log('🔵 [GOOGLE AUTH] Token payload keys:', Object.keys(payload));
            console.log('🔵 [GOOGLE AUTH] Token aud:', payload.aud);
            console.log('🔵 [GOOGLE AUTH] Token iss:', payload.iss);
            console.log('🔵 [GOOGLE AUTH] Token exp:', payload.exp, '(', new Date(payload.exp * 1000).toISOString(), ')');
          }
        } catch (decodeError) {
          console.log('🔵 [GOOGLE AUTH] ⚠️ Cannot decode JWT:', decodeError);
        }
      } else {
        console.log('🔵 [GOOGLE AUTH] ❌ IdToken is:', userInfo.idToken);
        console.log('🔵 [GOOGLE AUTH] ❌ Type:', typeof userInfo.idToken);
        console.log('🔵 [GOOGLE AUTH] ❌ Is null?', userInfo.idToken === null);
        console.log('🔵 [GOOGLE AUTH] ❌ Is undefined?', userInfo.idToken === undefined);
      }

      // Sérialisation complète
      console.log('🔵 [GOOGLE AUTH] Full userInfo JSON:');
      try {
        console.log(JSON.stringify(userInfo, null, 2));
      } catch (stringifyError) {
        console.log('🔵 [GOOGLE AUTH] ⚠️ Cannot stringify:', stringifyError);
        console.log('🔵 [GOOGLE AUTH] Raw object:', userInfo);
      }

      // Test avec getTokens()
      console.log('🔵 [GOOGLE AUTH] Testing getTokens() method...');
      try {
        const tokensAfterSignIn = await GoogleSignin.getTokens();
        console.log('🔵 [GOOGLE AUTH] getTokens() result:', {
          success: true,
          hasIdToken: !!tokensAfterSignIn?.idToken,
          hasAccessToken: !!tokensAfterSignIn?.accessToken,
          idTokenMatch: tokensAfterSignIn?.idToken === userInfo.idToken,
          tokensKeys: tokensAfterSignIn ? Object.keys(tokensAfterSignIn) : []
        });

        // WORKAROUND : Si idToken manque mais getTokens() l'a, l'utiliser
        if (!userInfo.idToken && tokensAfterSignIn?.idToken) {
          console.log('🔵 [GOOGLE AUTH] ✅ WORKAROUND: Using idToken from getTokens()');
          userInfo.idToken = tokensAfterSignIn.idToken;
        }
      } catch (getTokensError: any) {
        console.log('🔵 [GOOGLE AUTH] getTokens() error:', getTokensError?.message || getTokensError);
      }

      // Test avec getCurrentUser()
      console.log('🔵 [GOOGLE AUTH] Testing getCurrentUser() method...');
      try {
        const currentUser = await GoogleSignin.getCurrentUser();
        console.log('🔵 [GOOGLE AUTH] getCurrentUser() result:', {
          success: true,
          hasResult: !!currentUser,
          hasIdToken: !!currentUser?.idToken,
          idTokenMatch: currentUser?.idToken === userInfo.idToken,
          userKeys: currentUser ? Object.keys(currentUser) : []
        });

        // WORKAROUND : Si idToken manque mais getCurrentUser() l'a, l'utiliser
        if (!userInfo.idToken && currentUser?.idToken) {
          console.log('🔵 [GOOGLE AUTH] ✅ WORKAROUND: Using idToken from getCurrentUser()');
          userInfo.idToken = currentUser.idToken;
        }
      } catch (getCurrentUserError: any) {
        console.log('🔵 [GOOGLE AUTH] getCurrentUser() error:', getCurrentUserError?.message || getCurrentUserError);
      }

      console.log('🔵 [GOOGLE AUTH] ================================');
      console.log('🔵 [GOOGLE AUTH] END DIAGNOSTIC');
      console.log('🔵 [GOOGLE AUTH] Final idToken status:', !!userInfo.idToken);
      console.log('🔵 [GOOGLE AUTH] ================================');
      // ============================================
      // === FIN DIAGNOSTIC ===
      // ============================================

      const credentialReceivedTime = Date.now();
      const credentialDuration = credentialReceivedTime - requestStartTime;

      console.log('🔵 [GOOGLE AUTH] Google credentials received:', {
        hasIdToken: !!userInfo.idToken,
        hasUser: !!userInfo.user,
        email: userInfo.user?.email,
      });
      console.log('🔵 [GOOGLE AUTH] ⏱️ Time to get credentials:', credentialDuration + 'ms');

      if (!userInfo.idToken) {
        console.log('🔵 [GOOGLE AUTH] No ID token received - user may have cancelled');
        loginCoordinator.cancelLogin('User cancelled');
        return { success: false, error: 'CANCELLED' };
      }

      console.log('🔵 [GOOGLE AUTH] Calling manage-users endpoint with auth_social action...');
      const apiCallStartTime = Date.now();

      const googleAuthResponse = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'auth_social',
          googleIdToken: userInfo.idToken,
        }),
      });

      const apiCallEndTime = Date.now();
      const apiCallDuration = apiCallEndTime - apiCallStartTime;
      const totalDuration = apiCallEndTime - requestStartTime;

      console.log('🔵 [GOOGLE AUTH] ⏱️ API call duration:', apiCallDuration + 'ms');
      console.log('🔵 [GOOGLE AUTH] ⏱️ Total time:', totalDuration + 'ms');

      if (!googleAuthResponse.ok) {
        const errorData = await googleAuthResponse.json();

        if (errorData.errorType === 'ACCOUNT_MERGE_NOT_SUPPORTED') {
          console.log('🔵 [GOOGLE AUTH] Account merge not supported - user should login with original method');
          loginCoordinator.cancelLogin('Account merge not supported');
          return { success: false, error: t('accountMergeNotSupported') };
        }

        console.error('🔵 [GOOGLE AUTH] Authentication failed:', errorData);

        if (errorData.retryable) {
          loginCoordinator.cancelLogin('Google auth expired');
          return { success: false, error: errorData.details || 'Google authentication expired. Please try again.', shouldRetry: true };
        }

        loginCoordinator.cancelLogin(errorData.error || 'Google authentication failed');
        return { success: false, error: errorData.error || 'Google authentication failed' };
      }

      const googleAuthData = await googleAuthResponse.json();

      if (!googleAuthData.success) {
        loginCoordinator.cancelLogin(googleAuthData.error || 'Google authentication failed');
        return { success: false, error: googleAuthData.error || 'Google authentication failed' };
      }

      console.log('🔵 [GOOGLE AUTH] Authentication successful:', {
        isNewUser: googleAuthData.isNewUser,
        needsMarketingConsent: googleAuthData.needsMarketingConsent,
        accountLinked: googleAuthData.accountLinked,
        userId: googleAuthData.user.smartuserId,
      });

      const user: User = {
        id: googleAuthData.user.id,
        email: googleAuthData.user.email || '',
        phone_number: googleAuthData.user.phoneNumber || '',
        pseudo: googleAuthData.user.pseudo || generateDefaultPseudo(googleAuthData.user.smartuserId),
        isSubscribed: googleAuthData.user.isSubscribed,
        smartuserId: googleAuthData.user.smartuserId,
        paymentMethod: googleAuthData.user.paymentMethod || null,
      };

      if (googleAuthData.needsMarketingConsent) {
        console.log('🔵 [GOOGLE AUTH] Marketing consent required, deferring auth completion...');
        loginCoordinator.cancelLogin('Deferred for marketing consent');
        return {
          success: true,
          needsMarketingConsent: true,
          isNewUser: googleAuthData.isNewUser,
          pendingAuthData: {
            user,
            sessionToken: googleAuthData.sessionToken,
            isNewUser: googleAuthData.isNewUser,
          },
        };
      }

      await setStorageItem('token', googleAuthData.sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({
          user: user,
          token: googleAuthData.sessionToken,
          isLoading: false,
        });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);
        await trackDvLogin({
          type_of_action: googleAuthData.isNewUser ? 'account_creation' : 'login',
          method: 'manual_google',
          status: 'ok',
          type: 'login'
        });

        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn('Failed to track Google authentication event:', analyticsError);
      }

      if (Platform.OS !== 'web' && oneSignalService.isReady()) {
        try {
          await oneSignalService.login(user.smartuserId);
        } catch (oneSignalError) {
          console.warn('[OneSignal] Error during login flow:', oneSignalError);
        }
      }

      console.log(`🔵 [GOOGLE AUTH] ✅ ${googleAuthData.isNewUser ? 'Signup' : 'Signin'} successful`);
      if (googleAuthData.accountLinked) {
        console.log('🔵 [GOOGLE AUTH] ✅ Account successfully linked');
      }

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: googleAuthData.sessionToken,
        loginMethod: 'google',
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };

    } catch (error: any) {
      const errorMessage = error?.message || '';
      const isCancelled =
        error?.code === 'SIGN_IN_CANCELLED' ||
        errorMessage.includes('user canceled') ||
        errorMessage.includes('SIGN_IN_CANCELLED');

      if (isCancelled) {
        console.log('🔵 [GOOGLE AUTH] User cancelled sign in');
        loginCoordinator.cancelLogin('User cancelled');
        return { success: false, error: 'CANCELLED' };
      }

      console.error('🔵 [GOOGLE AUTH] ❌ Error:', error);
      loginCoordinator.cancelLogin(errorMessage || 'Google Sign In failed');

      try {
        await trackDvLogin({
          type_of_action: 'login',
          method: 'manual_google',
          status: 'ko',
          type: 'login'
        });
      } catch (analyticsError) {
        console.warn('Failed to track Google authentication failure event:', analyticsError);
      }

      const message = typeof errorMessage === 'string' ? errorMessage : 'Google Sign In failed';
      return { success: false, error: message };
    }
  };

  const loginWithOTPData = async (
    userData: OTPUserData,
    sessionToken: string,
    isNewUser: boolean,
    marketingConsent?: boolean | null
  ): Promise<{ success: boolean; error?: string }> => {
    loginCoordinator.beginLogin('otp');

    try {
      console.log('[OTP AUTH] Processing OTP login data:', {
        smartuserId: userData.smartuserId,
        hasEmail: !!userData.email,
        hasPhone: !!userData.phoneNumber,
        isNewUser,
      });

      const user: User = {
        id: userData.id || userData.smartuserId,
        email: userData.email || '',
        phone_number: userData.phoneNumber || '',
        pseudo: userData.pseudo || generateDefaultPseudo(userData.smartuserId),
        isSubscribed: userData.isSubscribed,
        smartuserId: userData.smartuserId,
        paymentMethod: userData.paymentMethod || null,
      };

      await setStorageItem('token', sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({
          user: user,
          token: sessionToken,
          isLoading: false,
        });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);

        const identityType = userData.email ? 'email' : 'msisdn';
        await trackDvLogin({
          type_of_action: isNewUser ? 'account_creation' : 'login',
          method: 'otp',
          status: 'ok',
          type: identityType,
          marketing_consent: marketingConsent ?? null,
          marketing_consent_shown: marketingConsent !== undefined,
        });

        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn('Failed to track OTP login event:', analyticsError);
      }

      if (Platform.OS !== 'web' && oneSignalService.isReady()) {
        try {
          console.log('[OneSignal] Logging in user after OTP:', user.smartuserId);
          await oneSignalService.login(user.smartuserId);
        } catch (oneSignalError) {
          console.warn('[OneSignal] Error during OTP login flow:', oneSignalError);
        }
      }

      console.log('[OTP AUTH] Login successful for user:', user.smartuserId);

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: sessionToken,
        loginMethod: 'otp',
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };

    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : 'OTP login failed';
      console.error('[OTP AUTH] Error:', error);
      loginCoordinator.cancelLogin(message);

      try {
        await trackDvLogin({
          type_of_action: 'login',
          method: 'otp',
          status: 'ko',
          type: 'null'
        });
      } catch (analyticsError) {
        console.warn('Failed to track OTP login failure event:', analyticsError);
      }

      return { success: false, error: message };
    }
  };

  const completeSocialAuthAfterMarketing = async (
    pendingAuthData: PendingSocialAuthData,
    authMethod: 'apple' | 'google',
    marketingConsent?: boolean | null
  ): Promise<{ success: boolean; error?: string }> => {
    loginCoordinator.beginLogin(authMethod);

    try {
      console.log(`[${authMethod.toUpperCase()} AUTH] Completing deferred authentication after marketing consent...`);

      const { user, sessionToken, isNewUser } = pendingAuthData;

      await setStorageItem('token', sessionToken);
      await setStorageItem('user', JSON.stringify(user));
      await deleteStorageItem('USER_LOGGED_OUT');

      if (isMountedRef.current) {
        setAuthState({
          user: user,
          token: sessionToken,
          isLoading: false,
        });
      }

      try {
        await setUserId(user.smartuserId);
        await setFacebookUserId(user.smartuserId);
        backgroundRefreshService.setCurrentUser(user.smartuserId);
        await trackDvLogin({
          type_of_action: isNewUser ? 'account_creation' : 'login',
          method: authMethod === 'apple' ? 'manual_appleconnect' : 'manual_google',
          status: 'ok',
          type: 'login',
          marketing_consent: marketingConsent ?? null,
          marketing_consent_shown: marketingConsent !== undefined,
        });

        bootSchedulerService.startBoot(user.smartuserId).catch(error => {
          console.warn('Boot scheduler failed (non-critical):', error);
        });
      } catch (analyticsError) {
        console.warn(`Failed to track ${authMethod} authentication event:`, analyticsError);
      }

      if (Platform.OS !== 'web' && oneSignalService.isReady()) {
        try {
          await oneSignalService.login(user.smartuserId);
        } catch (oneSignalError) {
          console.warn('[OneSignal] Error during login flow:', oneSignalError);
        }
      }

      console.log(`[${authMethod.toUpperCase()} AUTH] Deferred authentication completed successfully`);

      loginCoordinator.commitLogin({
        userId: user.smartuserId,
        sessionToken: sessionToken,
        loginMethod: authMethod,
        subscriptionStatus: user.isSubscribed ? 'paying' : 'free',
      });

      return { success: true };

    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : `${authMethod} authentication failed`;
      console.error(`[${authMethod.toUpperCase()} AUTH] Deferred authentication error:`, error);
      loginCoordinator.cancelLogin(message);

      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      logoutSignalService.signalLogout();
      clearCustomIdContext();

      console.log('🔐 Logout: Starting logout process:', {
        platform: Platform.OS,
        hasUser: !!authState.user,
        smartuserId: authState.user?.smartuserId
      });

      if (loginCoordinator.isLoginInProgress()) {
        console.log('🔐 Logout: Cancelling in-progress login transaction');
        loginCoordinator.cancelLogin('User logged out');
      }

      // OPTIMIZATION: Update UI state IMMEDIATELY for instant feedback
      // All cleanup operations will run in background without blocking
      if (isMountedRef.current) {
        setAuthState({
          user: null,
          token: null,
          isLoading: false,
        });
        console.log('🔐 Logout: ✅ Auth state cleared IMMEDIATELY (optimistic logout)');
      }

      // Run all cleanup operations in background without blocking
      // Use fire-and-forget pattern for maximum performance
      (async () => {
        try {
          // Set logout flag to prevent auto-login on next app launch
          console.log('🔐 Logout: Setting logout flag to prevent auto-reconnect');
          await setStorageItem('USER_LOGGED_OUT', 'true');

          // Clear session data in parallel for speed
          console.log('🔐 Logout: Clearing session data (token and user)');
          await Promise.allSettled([
            deleteStorageItem('token'),
            deleteStorageItem('user')
          ]);

          // Clear country detection cache synchronously (fast operation)
          clearCountryCache();
          console.log('🔐 Logout: Country cache cleared');

          // Clear biometric support cache (session-level cache)
          biometricSupportCache.clearCache();
          console.log('🔐 Logout: Biometric support cache cleared');

          // Clear subtitle preference from unified cache (user-specific data)
          import('@/services/unifiedStartupCache').then(({ unifiedStartupCache }) => {
            unifiedStartupCache.clearSubtitlePreference().catch(err => {
              console.warn('🔐 Logout: Error clearing subtitle preference:', err);
            });
          });

          // Clear user language preference cache
          import('@/services/userLanguagePreferenceCache').then(({ userLanguagePreferenceCache }) => {
            userLanguagePreferenceCache.clear().catch(err => {
              console.warn('🔐 Logout: Error clearing language preference cache:', err);
            });
          });

          // Notify background refresh service that user is logged out
          backgroundRefreshService.setCurrentUser(null);
          periodicRefreshService.setCurrentUser(null);

          // Clear any in-flight Supabase requests that may still resolve after logout
          import('@/services/supabaseRequestDeduplicator').then(({ supabaseRequestDeduplicator }) => {
            supabaseRequestDeduplicator.clearAllInFlightRequests();
          });

          // Clear user profile cache (gamification, notifications, profile)
          if (authState.user?.smartuserId) {
            userProfileCacheService.clearUserData(authState.user.smartuserId);
          }
          console.log('🔐 Logout: User-specific caches cleared');

          // Note: We preserve ALL biometric credentials so users can log back in with Face ID/Touch ID
          console.log('🔐 Logout: ✅ Biometric credentials preserved for manual biometric login');

          if (Platform.OS !== 'web') {
            import('@/services/revenueCatService').then(async ({ revenueCatService }) => {
              console.log('🛒 Logout: Resetting RevenueCat service in background...');

              if (Purchases) {
                try {
                  const isAnonymous = await Purchases.isAnonymous();
                  console.log('🛒 Logout: RevenueCat user isAnonymous:', isAnonymous);

                  if (!isAnonymous) {
                    await Purchases.logOut();
                    console.log('🛒 Logout: Logged out from RevenueCat SDK');
                  } else {
                    console.log('🛒 Logout: Skipping RevenueCat logout - user is anonymous');
                  }
                } catch (error) {
                  console.warn('🛒 Logout: RevenueCat SDK logout error (non-blocking):', error);
                }
              }

              revenueCatService.reset();
              console.log('🛒 Logout: RevenueCat service reset complete');
            }).catch(error => {
              console.error('🛒 Logout: Error importing RevenueCat service:', error);
            });
          }

          // Logout from OneSignal in background (non-blocking)
          if (Platform.OS !== 'web' && oneSignalService.isReady()) {
            oneSignalService.logout().catch(error => {
              console.warn('[OneSignal] Logout error (non-blocking):', error);
            });
          }

          // Track logout in background (non-blocking)
          // Group all analytics calls together without awaiting
          Promise.allSettled([
            trackDvLogin({
              type_of_action: 'logout',
              method: 'manual',
              status: 'ok',
              type: 'null'
            }),
            setUserId(null),
            setFacebookUserId(null)
          ]).then(() => {
            console.log('🔐 Logout: Analytics tracking completed');
          }).catch(analyticsError => {
            console.warn('Failed to track logout event:', analyticsError);
          });

          console.log('🔐 Logout: ✅ All background cleanup operations initiated');
        } catch (cleanupError) {
          console.error('🔐 Logout: Error during background cleanup:', cleanupError);
        }
      })();

      console.log('🔐 Logout: ✅ Logout completed instantly, cleanup running in background');
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, ensure we clear the auth state
      if (isMountedRef.current) {
        setAuthState({
          user: null,
          token: null,
          isLoading: false,
        });
      }
    }
  };

  const updateUserSubscription = (isSubscribed: boolean) => {
    if (authState.user && isMountedRef.current) {
      const updatedUser = { ...authState.user, isSubscribed };
      setAuthState(prev => ({
        ...prev,
        user: updatedUser
      }));
      
      // Update stored user data
      setStorageItem('user', JSON.stringify(updatedUser)).catch(error => {
        console.error('Error updating stored user data:', error);
      });
    }
  };

  const updateUserPaymentMethod = (paymentMethod: string | null) => {
    if (authState.user && isMountedRef.current) {
      const updatedUser = { ...authState.user, paymentMethod };
      setAuthState(prev => ({
        ...prev,
        user: updatedUser
      }));

      setStorageItem('user', JSON.stringify(updatedUser)).catch(error => {
        console.error('Error updating stored user payment method:', error);
      });
    }
  };

  const checkBiometricSupport = async () => {
    console.log('🔐 [checkBiometricSupport] Called, Platform.OS:', Platform.OS);

    return biometricSupportCache.getOrFetch(async () => {
      if (Platform.OS === 'web') {
        console.log('🔐 WebAuthn: Checking startRegistration availability:', {
          startRegistrationExists: !!startRegistration,
          startRegistrationType: typeof startRegistration,
          isFunction: typeof startRegistration === 'function'
        });

        if (!startRegistration || typeof startRegistration !== 'function') {
          console.error('🔐 WebAuthn: startRegistration is not available or not a function');
          return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
        }

        try {
          if (typeof window === 'undefined' || !window.PublicKeyCredential) {
            return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
          }

          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          const isEnrolled = await isBiometricEnabled();

          return {
            isAvailable: available,
            isEnrolled,
            supportedTypes: available ? ['WebAuthn'] : []
          };
        } catch (error) {
          console.error('Error checking WebAuthn support:', error);
          return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
        }
      } else if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
        console.log('🔐 [checkBiometricSupport] Unsupported platform, returning false');
        return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
      }

      console.log('🔐 [checkBiometricSupport] Checking LocalAuthentication module:', {
        moduleExists: !!LocalAuthentication,
        moduleType: typeof LocalAuthentication,
      });

      if (!LocalAuthentication) {
        console.error('🔐 [checkBiometricSupport] LocalAuthentication module is NULL - cannot check biometric support');
        return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
      }

      try {
        console.log('🔐 [checkBiometricSupport] Calling hasHardwareAsync...');
        const hasHardware = await LocalAuthentication.hasHardwareAsync();
        console.log('🔐 [checkBiometricSupport] hasHardware:', hasHardware);

        console.log('🔐 [checkBiometricSupport] Calling isEnrolledAsync...');
        const isEnrolled = await LocalAuthentication.isEnrolledAsync();
        console.log('🔐 [checkBiometricSupport] isEnrolled:', isEnrolled);

        console.log('🔐 [checkBiometricSupport] Calling supportedAuthenticationTypesAsync...');
        const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
        console.log('🔐 [checkBiometricSupport] supportedTypes raw:', supportedTypes);

        const typeNames = supportedTypes.map((type: number) => {
          switch (type) {
            case LocalAuthentication.AuthenticationType.FINGERPRINT:
              return Platform.OS === 'ios' ? 'Touch ID' : 'Fingerprint';
            case LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION:
              return 'Face ID';
            case LocalAuthentication.AuthenticationType.IRIS:
              return 'Iris';
            default:
              return 'Biometric';
          }
        });

        const result = {
          isAvailable: hasHardware && isEnrolled,
          isEnrolled,
          supportedTypes: typeNames
        };
        console.log('🔐 [checkBiometricSupport] Final result:', JSON.stringify(result));
        return result;
      } catch (error) {
        console.error('🔐 [checkBiometricSupport] Error checking biometric support:', error);
        return { isAvailable: false, isEnrolled: false, supportedTypes: [] };
      }
    });
  };

  const enableBiometricLogin = async (
    userParam?: User | null,
    tokenParam?: string | null
  ): Promise<boolean> => {
    const currentUser = userParam || authState.user;
    const currentToken = tokenParam || authState.token;

    console.log('🔐 [enableBiometricLogin] Starting biometric setup:', {
      hasUser: !!currentUser,
      hasToken: !!currentToken,
      smartuserId: currentUser?.smartuserId,
      platform: Platform.OS
    });

    if (!currentUser || !currentToken) {
      console.log('🔐 [enableBiometricLogin] ❌ Missing user or token');
      return false;
    }

    if (Platform.OS === 'web') {
      try {
        if (!startRegistration) {
          const module = await import('@simplewebauthn/browser');
          startRegistration = module.startRegistration;
          startAuthentication = module.startAuthentication;
        }

        const startResp = await fetch(
        `${supabaseUrl}/functions/v1/webauthn-register-start`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              smartuserId: currentUser.smartuserId,
              email: currentUser.email,
            }),
          }
        );

        if (!startResp.ok) {
          console.error('WebAuthn register-start failed:', await startResp.text());
          return false;
        }

        const options = await startResp.json();
        const attResp = await startRegistration(options);

        const finishResp = await fetch(
        `${supabaseUrl}/functions/v1/webauthn-register-finish`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              smartuserId: currentUser.smartuserId,
              attResp,
              expectedChallenge: options.challenge,
            }),
          }
        );

        if (!finishResp.ok) {
          console.error('WebAuthn register-finish failed:', await finishResp.text());
          return false;
        }

        const finishData = await finishResp.json();
        if (!finishData.success) {
          return false;
        }

        const biometricToken = `${currentToken}_${Date.now()}`;
        const storageKey = `BIOMETRIC_TOKEN_${currentUser.smartuserId}`;

        // Store in dedicated biometric keys (persistent across logout)
        await setStorageItem(`BIOMETRIC_USER_${currentUser.smartuserId}`, JSON.stringify(currentUser));
        await setStorageItem(`BIOMETRIC_SESSION_TOKEN_${currentUser.smartuserId}`, currentToken);
        await setStorageItem('LAST_BIOMETRIC_USER_ID', currentUser.smartuserId);
        await setStorageItem(storageKey, biometricToken);
        await setStorageItem('WEBAUTHN_ENABLED', 'true');

        // Also store in session keys for current session
        await setStorageItem('token', currentToken);
        await setStorageItem('user', JSON.stringify(currentUser));

        console.log('🔐 [enableBiometricLogin] ✅ WebAuthn credentials stored in dedicated keys');

        return true;
      } catch (error) {
        console.error('🔐 [enableBiometricLogin] ❌ Error enabling WebAuthn:', error);
        return false;
      }
    }

    if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
      return false;
    }

    try {
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: t('enableBiometricPrompt'),
        fallbackLabel: t('Use password'),
        disableDeviceFallback: false,
      });

      if (result.success) {
        const biometricToken = `${currentToken}_${Date.now()}`;
        const storageKey = `BIOMETRIC_TOKEN_${currentUser.smartuserId}`;

        // Store in dedicated biometric keys (persistent across logout)
        await setStorageItem(`BIOMETRIC_USER_${currentUser.smartuserId}`, JSON.stringify(currentUser));
        await setStorageItem(`BIOMETRIC_SESSION_TOKEN_${currentUser.smartuserId}`, currentToken);
        await setStorageItem('LAST_BIOMETRIC_USER_ID', currentUser.smartuserId);
        await setStorageItem(storageKey, biometricToken);
        await setStorageItem('BIOMETRIC_ENABLED', 'true');

        // Also store in session keys for current session
        await setStorageItem('token', currentToken);
        await setStorageItem('user', JSON.stringify(currentUser));

        console.log('🔐 [enableBiometricLogin] ✅ Biometric credentials stored in dedicated keys for user:', currentUser.smartuserId);

        // Track biometric setup
        try {
          await trackDvLogin({
            type_of_action: 'set_biometric',
            method: 'biometric',
            status: 'ok',
            type: 'null'
          });
        } catch (analyticsError) {
          console.warn('Failed to track biometric setup event:', analyticsError);
        }

        return true;
      }

      console.log('🔐 [enableBiometricLogin] ❌ Biometric authentication failed');

      // Track failed biometric setup
      try {
        await trackDvLogin({
          type_of_action: 'set_biometric',
          method: 'biometric',
          status: 'ko',
          type: 'null'
        });
      } catch (analyticsError) {
        console.warn('Failed to track biometric setup failure event:', analyticsError);
      }

      return false;
    } catch (error) {
      console.error('🔐 [enableBiometricLogin] ❌ Exception during biometric setup:', error);

      // Track biometric setup error
      try {
        await trackDvLogin({
          type_of_action: 'set_biometric',
          method: 'biometric',
          status: 'ko',
          type: 'null'
        });
      } catch (analyticsError) {
        console.warn('Failed to track biometric setup error event:', analyticsError);
      }

      return false;
    }
  };

  const disableBiometricLogin = async (): Promise<boolean> => {
    try {
      console.log('🔐 [disableBiometricLogin] Starting cleanup of all biometric data...');

      // Get the last biometric user ID to clean all related keys
      const lastUserId = await getStorageItem('LAST_BIOMETRIC_USER_ID');
      console.log('🔐 [disableBiometricLogin] Last biometric user ID:', lastUserId);

      // Delete platform-specific enable flag
      if (Platform.OS === 'web') {
        await deleteStorageItem('WEBAUTHN_ENABLED');
        console.log('🔐 [disableBiometricLogin] Deleted WEBAUTHN_ENABLED');
      } else {
        await deleteStorageItem('BIOMETRIC_ENABLED');
        console.log('🔐 [disableBiometricLogin] Deleted BIOMETRIC_ENABLED');
      }

      // Delete all user-specific biometric keys
      if (lastUserId) {
        await deleteStorageItem(`BIOMETRIC_USER_${lastUserId}`);
        await deleteStorageItem(`BIOMETRIC_SESSION_TOKEN_${lastUserId}`);
        await deleteStorageItem(`BIOMETRIC_TOKEN_${lastUserId}`);
        console.log('🔐 [disableBiometricLogin] Deleted all biometric keys for user:', lastUserId);
      }

      // Also clean current user's biometric keys if different from lastUserId
      if (authState.user && authState.user.smartuserId !== lastUserId) {
        await deleteStorageItem(`BIOMETRIC_USER_${authState.user.smartuserId}`);
        await deleteStorageItem(`BIOMETRIC_SESSION_TOKEN_${authState.user.smartuserId}`);
        await deleteStorageItem(`BIOMETRIC_TOKEN_${authState.user.smartuserId}`);
        console.log('🔐 [disableBiometricLogin] Also deleted biometric keys for current user:', authState.user.smartuserId);
      }

      // Delete the tracking key
      await deleteStorageItem('LAST_BIOMETRIC_USER_ID');
      console.log('🔐 [disableBiometricLogin] Deleted LAST_BIOMETRIC_USER_ID');

      console.log('🔐 [disableBiometricLogin] ✅ All biometric data cleaned successfully');
      return true;
    } catch (error) {
      console.error('🔐 [disableBiometricLogin] ❌ Error disabling biometric login:', error);
      return false;
    }
  };

  const performBiometricLoginInternal = async (): Promise<{ success: boolean; error?: string }> => {
    console.log('🔐 [performBiometricLogin] Starting biometric authentication:', {
      platform: Platform.OS
    });

    try {
      if (Platform.OS === 'web') {
        console.log('🔐 [performBiometricLogin] WebAuthn flow starting...');

        if (!startAuthentication) {
          const module = await import('@simplewebauthn/browser');
          startAuthentication = module.startAuthentication;
        }

        const startResp = await fetch(
          `${supabaseUrl}/functions/v1/webauthn-auth-start`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
          }
        );

        if (!startResp.ok) {
          console.log('🔐 [performBiometricLogin] ❌ WebAuthn start failed');
          return { success: false, error: 'Failed to start WebAuthn authentication' };
        }

        const options = await startResp.json();
        const authResp = await startAuthentication(options);

        const finishResp = await fetch(
          `${supabaseUrl}/functions/v1/webauthn-auth-finish`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              authResp,
              expectedChallenge: options.challenge,
            }),
          }
        );

        if (!finishResp.ok) {
          console.log('🔐 [performBiometricLogin] ❌ WebAuthn finish failed');
          return { success: false, error: 'WebAuthn authentication failed' };
        }

        const finishData = await finishResp.json();
        if (!finishData.success) {
          console.log('🔐 [performBiometricLogin] ❌ WebAuthn verification failed:', finishData.error);
          return { success: false, error: finishData.error || 'Authentication failed' };
        }

        // Check if account is disabled (deletion request pending)
        if (finishData.user.is_disabled === true) {
          console.log('🔐 [performBiometricLogin] ❌ Account is disabled');
          return {
            success: false,
            error: 'accountDisabled',
            errorType: 'ACCOUNT_DISABLED'
          };
        }

        const user: User = {
          id: finishData.user.id,
          email: finishData.user.email,
          phone_number: finishData.user.phone_number,
          pseudo: finishData.user.pseudo || generateDefaultPseudo(finishData.user.smartuserId),
          isSubscribed: finishData.user.isSubscribed,
          smartuserId: finishData.user.smartuserId,
          paymentMethod: finishData.user.paymentMethod || null,
        };

        await setStorageItem('token', finishData.sessionToken);
        await setStorageItem('user', JSON.stringify(user));

        // Clear logout flag on successful WebAuthn login
        await deleteStorageItem('USER_LOGGED_OUT');

        if (isMountedRef.current) {
          setAuthState({
            user: user,
            token: finishData.sessionToken,
            isLoading: false,
          });
        }

        console.log('🔐 [performBiometricLogin] ✅ WebAuthn login successful for user:', user.smartuserId);

        try {
          await setUserId(user.smartuserId);
          await setFacebookUserId(user.smartuserId);
          await trackDvLogin({
            type_of_action: 'login',
            method: 'biometric',
            status: 'ok',
            type: 'null'
          });
        } catch (analyticsError) {
          console.warn('Failed to track WebAuthn login event:', analyticsError);
        }

        return { success: true };
      }

      if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
        console.log('🔐 [performBiometricLogin] ❌ Platform not supported:', Platform.OS);
        return { success: false, error: 'Biometric authentication not supported on this platform' };
      }

      console.log('🔐 [performBiometricLogin] Native biometric flow starting...');

      // Get the last biometric user ID to know which credentials to load
      const lastUserId = await getStorageItem('LAST_BIOMETRIC_USER_ID');
      console.log('🔐 [performBiometricLogin] Last biometric user ID:', lastUserId);

      if (!lastUserId) {
        console.log('🔐 [performBiometricLogin] ❌ No biometric user ID found');
        return { success: false, error: 'No stored biometric authentication data found' };
      }

      // Read credentials from dedicated biometric keys
      const token = await getStorageItem(`BIOMETRIC_SESSION_TOKEN_${lastUserId}`);
      const userString = await getStorageItem(`BIOMETRIC_USER_${lastUserId}`);

      console.log('🔐 [performBiometricLogin] Biometric credentials check:', {
        hasToken: !!token,
        hasUser: !!userString,
        userId: lastUserId
      });

      if (!token || !userString) {
        console.log('🔐 [performBiometricLogin] ❌ Missing biometric credentials in storage');
        return { success: false, error: 'No stored biometric authentication data found' };
      }

      console.log('🔐 [performBiometricLogin] Showing biometric prompt to user...');

      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: t('Authenticate with biometrics'),
        fallbackLabel: t('Use password'),
        disableDeviceFallback: false,
      });

      console.log('🔐 [performBiometricLogin] Biometric prompt result:', {
        success: result.success,
        error: result.error
      });

      if (result.success) {
        const user = JSON.parse(userString);
        console.log('🔐 [performBiometricLogin] Biometric authentication successful, user:', user.smartuserId);

        // SECURITY: Check if account is disabled before allowing biometric login
        console.log('🔐 [performBiometricLogin] Verifying account status with backend...');
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/signin`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              emailOrPhone: user.email || user.phone_number,
              biometricToken: token,
              verifyOnly: true,
            }),
          });

          if (response.status === 403) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.error === 'accountDisabled') {
              console.log('🔐 [performBiometricLogin] ❌ Account verification failed: Account disabled');
              return {
                success: false,
                error: 'accountDisabled',
                errorType: 'ACCOUNT_DISABLED'
              };
            }
          }

          console.log('🔐 [performBiometricLogin] ✅ Account verification passed');
        } catch (verificationError) {
          console.warn('🔐 [performBiometricLogin] ⚠️ Account verification failed (continuing anyway):', verificationError);
          // Continue with login if verification fails (network issue, etc.)
        }

        // Store credentials in session keys for current session
        await setStorageItem('token', token);
        await setStorageItem('user', JSON.stringify(user));

        // Clear logout flag on successful biometric login
        await deleteStorageItem('USER_LOGGED_OUT');

        if (isMountedRef.current) {
          setAuthState({
            user,
            token,
            isLoading: false,
          });
        }

        console.log('🔐 [performBiometricLogin] ✅ Login successful, session established');

        // CRITICAL: Explicitly initialize RevenueCat with the user's smartuserId after biometric login
        // This ensures RevenueCat SDK is properly identified with the authenticated user
        // before the user proceeds to the app
        if (Platform.OS !== 'web' && user.smartuserId) {
          console.log('🔐 [performBiometricLogin] 🛒 Explicitly initializing RevenueCat for biometric login...');
          try {
            const initStartTime = Date.now();
            await revenueCatInitializer.forceInitialize(user.smartuserId);
            const initDuration = Date.now() - initStartTime;
            console.log('🔐 [performBiometricLogin] 🛒 ✅ RevenueCat initialization completed in', initDuration, 'ms');
          } catch (rcError) {
            console.error('🔐 [performBiometricLogin] 🛒 ⚠️ RevenueCat initialization failed (non-blocking):', rcError);
            // Don't block login if RevenueCat fails - the useEffect fallback will retry
          }
        }

        try {
          await setUserId(user.smartuserId);
          await setFacebookUserId(user.smartuserId);
          backgroundRefreshService.setCurrentUser(user.smartuserId);
          await trackDvLogin({
            type_of_action: 'login',
            method: 'biometric',
            status: 'ok',
            type: 'null'
          });

          // Start boot scheduler to load all user preferences (Phase 1) and background tasks
          bootSchedulerService.startBoot(user.smartuserId).catch(error => {
            console.warn('Boot scheduler failed (non-critical):', error);
          });
        } catch (analyticsError) {
          console.warn('Failed to track biometric login event:', analyticsError);
        }

        return { success: true };
      }

      console.log('🔐 [performBiometricLogin] ❌ Biometric authentication was not successful');
      return { success: false, error: result.error || 'Biometric authentication failed' };
    } catch (error: any) {
      console.error('🔐 [performBiometricLogin] ❌ Exception occurred:', error);

      try {
        await trackDvLogin({
          type_of_action: 'login',
          method: Platform.OS === 'web' ? 'webauthn' : 'biometric',
          status: 'ko',
          type: 'null'
        });
      } catch (analyticsError) {
        console.warn('Failed to track biometric login failure event:', analyticsError);
      }

      return { success: false, error: error.message || 'Biometric authentication failed' };
    }
  };

  const performBiometricLogin = async (): Promise<{ success: boolean; error?: string }> => {
    loginCoordinator.beginLogin('biometric');

    try {
      const result = await performBiometricLoginInternal();

      if (result.success) {
        const currentUser = authState.user;
        if (currentUser) {
          loginCoordinator.commitLogin({
            userId: currentUser.smartuserId,
            loginMethod: 'biometric',
            subscriptionStatus: currentUser.isSubscribed ? 'paying' : 'free',
          });
        } else {
          loginCoordinator.commitLogin({
            userId: 'unknown',
            loginMethod: 'biometric',
            subscriptionStatus: 'unknown',
          });
        }
      } else {
        loginCoordinator.cancelLogin(result.error || 'Biometric login failed');
      }

      return result;
    } catch (error: any) {
      loginCoordinator.cancelLogin(error?.message || 'Biometric login error');
      throw error;
    }
  };

  const isBiometricEnabled = async (): Promise<boolean> => {
    try {
      if (Platform.OS === 'web') {
        const enabled = await getStorageItem('WEBAUTHN_ENABLED');
        return enabled === 'true';
      } else {
        const enabled = await getStorageItem('BIOMETRIC_ENABLED');
        return enabled === 'true';
      }
    } catch (error) {
      console.error('Error checking biometric enabled status:', error);
      return false;
    }
  };

  const promptBiometricSetupIfAvailable = async (user: User, token: string) => {
    try {
      const biometricSupport = await checkBiometricSupport();
      const isAlreadyEnabled = await isBiometricEnabled();

      if (biometricSupport.isAvailable && !isAlreadyEnabled) {
        const authType = Platform.OS === 'web' ? 'WebAuthn' : 
                        biometricSupport.supportedTypes.includes('Face ID') ? 'Face ID' : 
                        biometricSupport.supportedTypes[0] || 'Biometric';

        Alert.alert(
          t('Enable {{authType}}?', { authType }),
          t('Would you like to enable {{authType}} for faster login?', { authType }),
          [
            {
              text: t('Not now'),
              style: 'cancel',
            },
            {
              text: t('Enable'),
              onPress: async () => {
                const success = await enableBiometricLogin(user, token);
                if (success) {
                  Alert.alert(
                    t('Success'),
                    t('{{authType}} login has been enabled!', { authType })
                  );
                } else {
                  Alert.alert(
                    t('Error'),
                    t('Failed to enable {{authType}} login. Please try again later.', { authType })
                  );
                }
              },
            },
          ]
        );
      }
    } catch (error) {
      console.error('Error prompting biometric setup:', error);
    }
  };

  const value = {
    authState,
    signInWithApple,
    signInWithGoogle,
    completeSocialAuthAfterMarketing,
    loginWithOTPData,
    logout,
    updateUserSubscription,
    updateUserPaymentMethod,
    checkBiometricSupport,
    enableBiometricLogin,
    disableBiometricLogin,
    performBiometricLogin,
    isBiometricEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};